import { useCallback, useRef } from 'react';
import throttle from 'lodash/throttle';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { loadPostCollectionAsync, resetPostCollection } from '../../data-layer/posts/actions';
import {
    selectPostCollection,
    selectPostCollectionLoading,
} from '../../data-layer/posts/selectors';
import { useDispatch } from '../../data-layer/useDispatch';
import { useSelector } from '../../data-layer/useSelector';
import type { PostModel } from '../../data-layer/posts/types';

const THROTTLE_TIMEOUT = 1000;

export type PostCollectionFilters = Omit<Parameters<typeof loadPostCollectionAsync>[1], 'next'>;

export interface UsePostsCollectionPayload {
    items: PostModel[];
    isLoading: boolean;
    isEndReached: boolean;
    onReset: VoidFunction;
    filters: Record<string, any>;
    onLoadNext: (params?: Record<string, any>) => void;
    onUpdateQuery: (filters: Partial<PostCollectionFilters>) => void;
    next: string;
}

interface UsePostsCollectionProps {
    namespace: string;
    filters: PostCollectionFilters;
}

export const usePostCollection = (params: UsePostsCollectionProps): UsePostsCollectionPayload => {
    const { namespace } = params;
    const filtersRef = useRef<PostCollectionFilters>(params.filters);
    const dispatch = useDispatch();
    const { items = [], next } = useSelector((state) => selectPostCollection(state, namespace));
    const isLoading = useSelector((state) => selectPostCollectionLoading(state, namespace));
    const isEndReached = items.length !== 0 && !next;
    const onLoadNext = useCallback(
        throttle(
            () =>
                dispatch(
                    loadPostCollectionAsync(namespace, {
                        ...filtersRef.current,
                        next,
                    }),
                ),
            THROTTLE_TIMEOUT,
        ),
        [dispatch, filtersRef, next],
    );

    const onReset = useCallback(() => dispatch(resetPostCollection(namespace)), [dispatch]);

    const onUpdateQuery = useCallback(
        throttle((filters: Partial<PostCollectionFilters>) => {
            onReset();
            filtersRef.current = {
                ...filtersRef.current,
                ...filters,
            };
            dispatch(loadPostCollectionAsync(namespace, filtersRef.current));
        }, THROTTLE_TIMEOUT),
        [dispatch, onReset],
    );

    // TODO: Позже сделать первоначальную загрузку на базе бесконечных списков/сеток
    useEffectOnce(() => {
        if (items.length === 0) {
            onLoadNext();
        }
    });

    return {
        isLoading,
        items,
        onReset,
        onLoadNext,
        onUpdateQuery,
        isEndReached,
        filters: filtersRef.current,
        next,
    };
};
